// Referrals page — who brought in whom, what it's worth, and what more of it
// would be worth. Three stacked sections:
//
//   1. Referral map: v_referral_edges drawn as an SVG forest. Referrers who
//      were not themselves referred are roots (left column); every hop to the
//      right is one referral deep. The forest is built client-side with a
//      VISITED SET so a bad edge cycle can never infinite-loop (depth capped
//      at MAX_DEPTH; circular or over-deep edges are dropped and counted in a
//      warning). Each card shows the person's own LTV and — for referrers —
//      "brought in: $X", the LTV sum of their WHOLE subtree of descendants.
//      Hover traces the full ancestor chain, click opens the client detail
//      page, and the − / + toggle collapses a branch. Depth picks the color
//      from the app's pastel palette.
//   2. What-if model ("referral engine"): v_referral_stats + a slider from
//      today's referrer percentage up to 25% — how many more referrers that
//      is, projected new clients, projected added revenue at the current
//      average, and net after the $100 + $100 referral credits ($200 per
//      successful referral). Clearly labeled as estimates.
//   3. Referral goal: settings key 'referral_goal' (bare number as text),
//      edited inline and upserted on key; progress bar = referred_count/goal.
//
// All dollar figures go through the shared privacy mask (usePrivacy/maskMoney)
// exactly like Home/Marketing/Ads; counts and percentages stay visible.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { money } from "../lib/format";
import { maskMoney, usePrivacy } from "../lib/privacy";
import { fmtDate } from "../lib/dates";
import type { Id } from "../lib/types";

const GOAL_KEY = "referral_goal";
/** $100 off for the referrer + $100 off for the friend. */
const CREDIT_PER_REFERRAL = 200;
/** Defensive chain cap — edges deeper than this (or circular) are dropped. */
const MAX_DEPTH = 8;

/* Map geometry (SVG pixels). */
const NODE_W = 184;
const NODE_H = 64;
const COL_GAP = 72;
const ROW_GAP = 16;
const PAD = 18;
/** Extra row slots of breathing room between separate trees. */
const TREE_GAP = 0.45;

/** Depth-based color progression from the app's pastel palette (cycled). */
const DEPTH_COLORS: { fill: string; stroke: string; ink: string }[] = [
  { fill: "var(--lavender-soft)", stroke: "var(--lavender)", ink: "var(--lavender-ink)" },
  { fill: "var(--sky-soft)", stroke: "var(--sky)", ink: "var(--sky-ink)" },
  { fill: "var(--mint-soft)", stroke: "var(--mint)", ink: "var(--mint-ink)" },
  { fill: "var(--peach-soft)", stroke: "var(--peach)", ink: "var(--peach-ink)" },
  { fill: "var(--rose-soft)", stroke: "var(--rose)", ink: "var(--rose-ink)" },
  { fill: "var(--butter-soft)", stroke: "var(--butter)", ink: "var(--butter-ink)" },
];

/* ------------------------------------------------------------------ helpers */

/** PostgREST can serialize numerics as strings; coalesce to a number or null. */
function num(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

/** Whole-dollar money for the compact map cards ("$1,234"; "—" when unknown). */
function shortMoney(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const abs = Math.round(Math.abs(v)).toLocaleString("en-US");
  return `${v < 0 ? "-" : ""}$${abs}`;
}

function clipName(name: string, max = 19): string {
  return name.length > max ? `${name.slice(0, max - 1).trimEnd()}…` : name;
}

function pct1(v: number): string {
  return v.toFixed(1);
}

/* -------------------------------------------------------------------- types */

/** One row of `v_referral_edges`. */
interface EdgeRow {
  referred_client_id: Id;
  referrer_client_id: Id;
  note: string | null;
  created_at: string;
  referred_name: string | null;
  referrer_name: string | null;
  /** SUM(sales.sale_total) over the referred client's canonical sales. */
  referred_ltv: number | string | null;
  referred_first_visit: string | null;
}

/** The single row of `v_referral_stats`. */
interface StatsRow {
  active_clients: number | string | null;
  referrer_count: number | string | null;
  referred_count: number | string | null;
  referred_revenue: number | string | null;
}

/* ------------------------------------------------------------- forest build */

interface TreeNode {
  key: string;
  id: Id;
  name: string;
  /** The person's OWN lifetime value (null when unknown). */
  ltv: number | null;
  note: string | null;
  firstVisit: string | null;
  depth: number;
  children: TreeNode[];
  /** LTV sum of ALL descendants (whole subtree, self excluded). */
  broughtIn: number;
  descendants: number;
}

/**
 * Build the referral forest. One parent per person (referred_client_id is the
 * table's PK), so trees are the normal case — the visited set + depth cap are
 * pure defense against bad data, and every edge they skip is counted so the
 * UI can warn instead of silently hiding people.
 */
function buildForest(
  edges: EdgeRow[],
  rootLtv: ReadonlyMap<string, number | null>,
): { roots: TreeNode[]; dropped: number } {
  const childEdges = new Map<string, EdgeRow[]>();
  const hasParent = new Set<string>();
  const names = new Map<string, string>();
  const idByKey = new Map<string, Id>();

  for (const e of edges) {
    const rk = String(e.referrer_client_id);
    const ck = String(e.referred_client_id);
    idByKey.set(rk, e.referrer_client_id);
    idByKey.set(ck, e.referred_client_id);
    const rName = (e.referrer_name ?? "").trim();
    if (rName) names.set(rk, rName);
    const cName = (e.referred_name ?? "").trim();
    if (cName) names.set(ck, cName);
    hasParent.add(ck);
    const list = childEdges.get(rk);
    if (list) list.push(e);
    else childEdges.set(rk, [e]);
  }

  const visited = new Set<string>();
  let builtEdges = 0; // every non-root node rendered = exactly one edge kept

  function build(
    key: string,
    ltv: number | null,
    note: string | null,
    firstVisit: string | null,
    depth: number,
  ): TreeNode {
    visited.add(key);
    const node: TreeNode = {
      key,
      id: idByKey.get(key) ?? key,
      name: names.get(key) ?? `Client ${key}`,
      ltv,
      note,
      firstVisit,
      depth,
      children: [],
      broughtIn: 0,
      descendants: 0,
    };
    if (depth >= MAX_DEPTH) return node; // children beyond the cap are dropped
    const kids = [...(childEdges.get(key) ?? [])].sort((a, b) =>
      a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0,
    );
    for (const e of kids) {
      const ck = String(e.referred_client_id);
      if (visited.has(ck)) continue; // cycle in the data — never loop
      const child = build(ck, num(e.referred_ltv), e.note, e.referred_first_visit, depth + 1);
      builtEdges++;
      node.children.push(child);
      node.broughtIn += (child.ltv ?? 0) + child.broughtIn;
      node.descendants += 1 + child.descendants;
    }
    return node;
  }

  const roots = [...childEdges.keys()]
    .filter((k) => !hasParent.has(k))
    .map((k) => build(k, rootLtv.get(k) ?? null, null, null, 0));
  roots.sort((a, b) => b.broughtIn - a.broughtIn || a.name.localeCompare(b.name));
  return { roots, dropped: edges.length - builtEdges };
}

/* ------------------------------------------------------------------- layout */

interface PlacedNode {
  node: TreeNode;
  col: number;
  /** Fractional row slot; parents sit at the midpoint of their children. */
  row: number;
  collapsed: boolean;
  parentKey: string | null;
}

interface PlacedLink {
  from: PlacedNode;
  to: PlacedNode;
}

/** Tidy left-to-right tree layout: leaves take row slots, parents center. */
function layoutForest(
  roots: TreeNode[],
  collapsed: ReadonlySet<string>,
): { nodes: PlacedNode[]; links: PlacedLink[]; maxCol: number; maxRow: number } {
  const nodes: PlacedNode[] = [];
  const links: PlacedLink[] = [];
  let cursor = 0;
  let maxCol = 0;
  let maxRow = 0;

  function place(n: TreeNode, parentKey: string | null): PlacedNode {
    const isCollapsed = collapsed.has(n.key) && n.children.length > 0;
    const p: PlacedNode = { node: n, col: n.depth, row: 0, collapsed: isCollapsed, parentKey };
    const visibleKids = isCollapsed ? [] : n.children;
    if (visibleKids.length === 0) {
      p.row = cursor;
      cursor += 1;
    } else {
      const placedKids = visibleKids.map((c) => {
        const cp = place(c, n.key);
        links.push({ from: p, to: cp });
        return cp;
      });
      p.row = (placedKids[0].row + placedKids[placedKids.length - 1].row) / 2;
    }
    nodes.push(p);
    maxCol = Math.max(maxCol, p.col);
    maxRow = Math.max(maxRow, p.row);
    return p;
  }

  roots.forEach((r, i) => {
    if (i > 0) cursor += TREE_GAP;
    place(r, null);
  });
  return { nodes, links, maxCol, maxRow };
}

const xOf = (col: number) => PAD + col * (NODE_W + COL_GAP);
const yOf = (row: number) => PAD + row * (NODE_H + ROW_GAP);

/** Smooth cubic connector from a parent's right edge to a child's left edge. */
function linkPath(from: PlacedNode, to: PlacedNode): string {
  const x1 = xOf(from.col) + NODE_W;
  const y1 = yOf(from.row) + NODE_H / 2;
  const x2 = xOf(to.col);
  const y2 = yOf(to.row) + NODE_H / 2;
  const mx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}

/* --------------------------------------------------------------------- page */

export default function Referrals() {
  const navigate = useNavigate();
  // Privacy mode (MetricsBar 👁 toggle): dollar figures mask to "$ ••••".
  const [privacy] = usePrivacy();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [collapsedKeys, setCollapsedKeys] = useState<ReadonlySet<string>>(new Set());
  const [targetPct, setTargetPct] = useState<number | null>(null);
  const [goalDraft, setGoalDraft] = useState("");
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalError, setGoalError] = useState<string | null>(null);

  const dollars = (v: number | string | null | undefined) => maskMoney(money(v), privacy);
  const mapDollars = (v: number | null | undefined) => maskMoney(shortMoney(v), privacy);

  /* ------------------------------------------------------------------ data */

  const edges = useQuery<EdgeRow[]>(async () => {
    const { data, error } = await supabase
      .from("v_referral_edges")
      .select("*")
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as EdgeRow[];
  }, []);

  const stats = useQuery<StatsRow | null>(async () => {
    const { data, error } = await supabase.from("v_referral_stats").select("*").maybeSingle();
    if (error) throw new Error(error.message);
    return (data ?? null) as StatsRow | null;
  }, []);

  // Root referrers were never referred themselves, so v_referral_edges has no
  // LTV for them — fetch it from v_client_list (canonical + non-internal).
  const rootIds = useMemo(() => {
    const rows = edges.data ?? [];
    const referred = new Set(rows.map((e) => String(e.referred_client_id)));
    const seen = new Set<string>();
    const ids: Id[] = [];
    for (const e of rows) {
      const k = String(e.referrer_client_id);
      if (!referred.has(k) && !seen.has(k)) {
        seen.add(k);
        ids.push(e.referrer_client_id);
      }
    }
    return ids;
  }, [edges.data]);

  const rootLtv = useQuery<Map<string, number | null>>(async () => {
    if (rootIds.length === 0) return new Map<string, number | null>();
    const { data, error } = await supabase.from("v_client_list").select("id, ltv").in("id", rootIds);
    if (error) throw new Error(error.message);
    const m = new Map<string, number | null>();
    for (const r of (data ?? []) as { id: Id; ltv: number | string | null }[]) {
      m.set(String(r.id), num(r.ltv));
    }
    return m;
  }, [rootIds]);

  // Current goal: bare number as text; missing row (or junk) = unset.
  const goalQ = useQuery<number | null>(async () => {
    const { data, error } = await supabase
      .from("settings")
      .select("*")
      .eq("key", GOAL_KEY)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    const raw = (data as Record<string, unknown>)["value"];
    const txt = typeof raw === "string" ? raw.replace(/^"+|"+$/g, "").trim() : String(raw ?? "");
    const n = Number(txt);
    return txt !== "" && Number.isFinite(n) && n > 0 ? n : null;
  }, []);

  useEffect(() => {
    if (goalQ.data != null) setGoalDraft(String(goalQ.data));
  }, [goalQ.data]);

  /* ------------------------------------------------------------------- map */

  const forest = useMemo(
    () => buildForest(edges.data ?? [], rootLtv.data ?? new Map<string, number | null>()),
    [edges.data, rootLtv.data],
  );

  const layouted = useMemo(
    () => layoutForest(forest.roots, collapsedKeys),
    [forest, collapsedKeys],
  );

  // Hover → the full ancestor chain (hovered node up to its root).
  const chain = useMemo<ReadonlySet<string> | null>(() => {
    if (hoveredKey == null) return null;
    const parentByKey = new Map<string, string | null>();
    for (const p of layouted.nodes) parentByKey.set(p.node.key, p.parentKey);
    const set = new Set<string>();
    let k: string | null = hoveredKey;
    while (k != null && !set.has(k)) {
      set.add(k);
      k = parentByKey.get(k) ?? null;
    }
    return set;
  }, [hoveredKey, layouted]);

  function toggleCollapsed(key: string) {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const svgW = PAD * 2 + (layouted.maxCol + 1) * NODE_W + layouted.maxCol * COL_GAP;
  const svgH = PAD * 2 + layouted.maxRow * (NODE_H + ROW_GAP) + NODE_H;

  /* --------------------------------------------------------------- what-if */

  const s = stats.data;
  const activeClients = num(s?.active_clients) ?? 0;
  const referrerCount = num(s?.referrer_count) ?? 0;
  const referredCount = num(s?.referred_count) ?? 0;
  const referredRevenue = num(s?.referred_revenue) ?? 0;

  const currentPct = activeClients > 0 ? (referrerCount / activeClients) * 100 : 0;
  const avgPerReferred = referredCount > 0 ? referredRevenue / referredCount : null;

  const maxPct = Math.max(25, Math.ceil(currentPct));
  const minPct = Number(Math.min(currentPct, maxPct).toFixed(1));
  const target = Math.min(Math.max(targetPct ?? currentPct, minPct), maxPct);

  // Projections — estimates based on current averages, as labeled in the UI.
  const friendsPerReferrer =
    referrerCount > 0 && referredCount > 0 ? referredCount / referrerCount : 1;
  const targetReferrers = Math.ceil((target / 100) * activeClients);
  const moreReferrers = Math.max(0, targetReferrers - referrerCount);
  const addedClients = Math.round(moreReferrers * friendsPerReferrer);
  const addedRevenue = avgPerReferred != null ? addedClients * avgPerReferred : null;
  const creditCost = addedClients * CREDIT_PER_REFERRAL;
  const netAfterCredits = addedRevenue != null ? addedRevenue - creditCost : null;

  /* ------------------------------------------------------------------ goal */

  const goal = goalQ.data;
  const goalNum = Number(goalDraft);
  const goalValid = goalDraft.trim() !== "" && Number.isInteger(goalNum) && goalNum >= 1;
  const goalDirty = goalDraft !== (goal != null ? String(goal) : "");
  const progressPct = goal != null && goal > 0 ? (referredCount / goal) * 100 : null;

  async function saveGoal() {
    if (!goalValid) return;
    setGoalSaving(true);
    setGoalError(null);
    const { error } = await supabase
      .from("settings")
      .upsert({ key: GOAL_KEY, value: String(goalNum) }, { onConflict: "key" });
    setGoalSaving(false);
    if (error) {
      setGoalError(error.message);
      return;
    }
    goalQ.reload();
  }

  /* ---------------------------------------------------------------- render */

  return (
    <div className="page">
      <style>{`
        @keyframes refIn { from { opacity: 0; } to { opacity: 1; } }
        .ref-enter { animation: refIn 0.45s ease both; }
        .ref-node { cursor: pointer; }
        .ref-node rect { transition: stroke 0.15s ease, stroke-width 0.15s ease; }
        .ref-link { transition: stroke 0.15s ease, stroke-width 0.15s ease, opacity 0.15s ease; }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Referrals</h1>
          <div className="page-sub">
            Who brought in whom, the revenue each chain produced, and what more referrals would be
            worth.
          </div>
        </div>
      </div>

      {/* -------------------------------------------------- 1. referral map */}
      <div className="panel">
        <div className="panel-title">Referral map</div>
        {edges.error && <div className="error-banner">{edges.error}</div>}
        {rootLtv.error && (
          <div className="field-hint amber mb-1">
            Root referrers' own LTVs could not load ({rootLtv.error}) — their cards show “—”.
          </div>
        )}
        {forest.dropped > 0 && (
          <div className="field-hint amber mb-1">
            {forest.dropped} referral {forest.dropped === 1 ? "link" : "links"} hidden — circular or
            deeper than {MAX_DEPTH} hops. That is bad data worth fixing in client_referrals.
          </div>
        )}
        {edges.loading && !edges.data ? (
          <div className="loading">
            <span className="spinner" /> Loading referral map…
          </div>
        ) : layouted.nodes.length === 0 ? (
          <div className="empty-state">
            No referrals mapped yet. As referrals are recorded (who brought in whom), every chain
            grows a tree here — the referrer on the left, everyone they brought in branching to the
            right, with the revenue each branch produced.
          </div>
        ) : (
          <>
            <p className="muted small">
              Roots on the left started a chain; each column to the right is one referral hop.
              “Brought in” totals the lifetime value of everyone downstream of that person.
            </p>
            <div style={{ overflowX: "auto", paddingBottom: 4 }}>
              <svg
                width={svgW}
                height={svgH}
                viewBox={`0 0 ${svgW} ${svgH}`}
                role="img"
                aria-label="Referral map — who referred whom"
              >
                {/* connector lines under the cards */}
                {layouted.links.map((l) => {
                  const lit =
                    chain != null && chain.has(l.to.node.key) && chain.has(l.from.node.key);
                  const dim = chain != null && !lit;
                  const c = DEPTH_COLORS[l.from.node.depth % DEPTH_COLORS.length];
                  return (
                    <path
                      key={`${l.from.node.key}->${l.to.node.key}`}
                      className="ref-link ref-enter"
                      style={{ animationDelay: `${Math.min(l.to.node.depth * 80, 640)}ms` }}
                      d={linkPath(l.from, l.to)}
                      fill="none"
                      stroke={lit ? "var(--accent)" : c.stroke}
                      strokeWidth={lit ? 2.5 : 1.6}
                      opacity={dim ? 0.25 : 0.95}
                    />
                  );
                })}

                {/* person cards */}
                {layouted.nodes.map((p) => {
                  const c = DEPTH_COLORS[p.node.depth % DEPTH_COLORS.length];
                  const x = xOf(p.col);
                  const y = yOf(p.row);
                  const inChain = chain?.has(p.node.key) ?? false;
                  const dim = chain != null && !inChain;
                  const n = p.node;
                  return (
                    <g key={n.key} transform={`translate(${x},${y})`}>
                      <g
                        className="ref-node ref-enter"
                        style={{
                          animationDelay: `${Math.min(n.depth * 80, 640)}ms`,
                          opacity: dim ? 0.35 : 1,
                          transition: "opacity 0.15s ease",
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${n.name}`}
                        onMouseEnter={() => setHoveredKey(n.key)}
                        onMouseLeave={() => setHoveredKey(null)}
                        onClick={() => navigate(`/clients/${String(n.id)}`)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") navigate(`/clients/${String(n.id)}`);
                        }}
                      >
                        <title>
                          {`${n.name} — LTV ${mapDollars(n.ltv)}` +
                            (n.firstVisit ? ` · first visit ${fmtDate(n.firstVisit)}` : "") +
                            (n.note ? ` · ${n.note}` : "") +
                            " · click to open"}
                        </title>
                        <rect
                          width={NODE_W}
                          height={NODE_H}
                          rx={13}
                          fill={c.fill}
                          stroke={inChain ? "var(--accent)" : c.stroke}
                          strokeWidth={inChain ? 2 : 1.25}
                        />
                        <text x={12} y={21} fontSize={12.5} fontWeight={700} fill={c.ink}>
                          {clipName(n.name)}
                        </text>
                        <text x={12} y={37} fontSize={11} fill={c.ink} opacity={0.8}>
                          LTV {mapDollars(n.ltv)}
                        </text>
                        {n.descendants > 0 && (
                          <text x={12} y={53} fontSize={11} fontWeight={650} fill={c.ink}>
                            brought in: {mapDollars(n.broughtIn)} · {n.descendants}{" "}
                            {n.descendants === 1 ? "referral" : "referrals"}
                          </text>
                        )}
                      </g>
                      {n.children.length > 0 && (
                        <g
                          style={{ cursor: "pointer" }}
                          role="button"
                          tabIndex={0}
                          aria-label={p.collapsed ? `Expand ${n.name}'s branch` : `Collapse ${n.name}'s branch`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCollapsed(n.key);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              toggleCollapsed(n.key);
                            }
                          }}
                        >
                          <title>{p.collapsed ? "Expand branch" : "Collapse branch"}</title>
                          <circle
                            cx={NODE_W}
                            cy={NODE_H / 2}
                            r={10}
                            fill="var(--panel)"
                            stroke={c.stroke}
                            strokeWidth={1.25}
                          />
                          <text
                            x={NODE_W}
                            y={NODE_H / 2 + 3.5}
                            fontSize={p.collapsed ? 9.5 : 12}
                            fontWeight={700}
                            textAnchor="middle"
                            fill={c.ink}
                          >
                            {p.collapsed
                              ? `+${n.descendants <= 99 ? n.descendants : ""}`
                              : "−"}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
          </>
        )}
        <div className="table-footer">
          <span>Click a card to open the client · hover to trace a chain · − / + collapses a branch.</span>
          <button
            type="button"
            className="btn-link small"
            onClick={() => {
              edges.reload();
              stats.reload();
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* ------------------------------------------------- 2. what-if model */}
      <div className="panel mt-2">
        <div className="panel-title">Referral engine — what if more clients referred?</div>
        {stats.error && <div className="error-banner">{stats.error}</div>}
        {stats.loading && !stats.data ? (
          <div className="loading">
            <span className="spinner" /> Loading referral stats…
          </div>
        ) : (
          <>
            <p className="muted small">
              {referrerCount} of {activeClients} active clients have referred someone (
              {pct1(currentPct)}%). Each successful referral costs $200 in credits — $100 off for
              the referrer, $100 off for the friend.
            </p>

            <div className="home-metric-grid">
              <div className="metric-card grad-lavender">
                <div className="metric-card-label">Clients who referred</div>
                <div className="metric-card-value">
                  {referrerCount} of {activeClients}
                </div>
                <div className="metric-card-sub">{pct1(currentPct)}% of active clients</div>
              </div>
              <div className="metric-card grad-sky">
                <div className="metric-card-label">Clients referred in</div>
                <div className="metric-card-value">{referredCount}</div>
                <div className="metric-card-sub">arrived through a referral</div>
              </div>
              <div className="metric-card grad-mint">
                <div className="metric-card-label">Referral revenue</div>
                <div className="metric-card-value">{dollars(referredRevenue)}</div>
                <div className="metric-card-sub">everything referred clients spent</div>
              </div>
              <div className="metric-card grad-peach">
                <div className="metric-card-label">Avg per referred client</div>
                <div className="metric-card-value">
                  {avgPerReferred == null ? "—" : dollars(avgPerReferred)}
                </div>
                <div className="metric-card-sub">lifetime value so far</div>
              </div>
            </div>

            <div className="mt-2">
              <label htmlFor="referral-target" className="bold">
                Target: {pct1(target)}% of active clients referring
              </label>
              <input
                id="referral-target"
                type="range"
                min={minPct}
                max={maxPct}
                step={0.1}
                value={target}
                disabled={activeClients === 0}
                onChange={(e) => setTargetPct(Number(e.target.value))}
                style={{ width: "100%", accentColor: "var(--accent)", marginTop: 6 }}
              />
              <div className="flex" style={{ justifyContent: "space-between" }}>
                <span className="muted small">{pct1(minPct)}% — today</span>
                <span className="muted small">{pct1(maxPct)}%</span>
              </div>
            </div>

            <div className="home-metric-grid mt-2">
              <div className="metric-card grad-butter">
                <div className="metric-card-label">New referrers needed</div>
                <div className="metric-card-value">+{moreReferrers}</div>
                <div className="metric-card-sub">
                  {moreReferrers === 1 ? "client" : "clients"} nudged into referring
                </div>
              </div>
              <div className="metric-card grad-lavender">
                <div className="metric-card-label">Projected new clients</div>
                <div className="metric-card-value">+{addedClients}</div>
                <div className="metric-card-sub">
                  ≈ {friendsPerReferrer.toFixed(1)}{" "}
                  {friendsPerReferrer === 1 ? "friend" : "friends"} per new referrer
                </div>
              </div>
              <div className="metric-card grad-mint">
                <div className="metric-card-label">Projected added revenue</div>
                <div className="metric-card-value">
                  {addedRevenue == null ? "—" : dollars(addedRevenue)}
                </div>
                <div className="metric-card-sub">at today's avg per referred client</div>
              </div>
              <div className="metric-card grad-sky">
                <div className="metric-card-label">Net after credits</div>
                <div className="metric-card-value">
                  {netAfterCredits == null ? "—" : dollars(netAfterCredits)}
                </div>
                <div className="metric-card-sub">minus {dollars(creditCost)} in referral credits</div>
              </div>
            </div>

            <div className="field-hint mt-1">
              Estimates based on current averages: each new referrer is assumed to bring{" "}
              {friendsPerReferrer.toFixed(1)}{" "}
              {friendsPerReferrer === 1 ? "friend" : "friends"} who spend like today's referred
              clients, and every successful referral costs $200 ($100 + $100) in credits.
              {avgPerReferred == null &&
                " No referred revenue yet, so revenue projections stay “—” until a referred client buys."}
            </div>
          </>
        )}
      </div>

      {/* -------------------------------------------------- 3. referral goal */}
      <div className="panel mt-2">
        <div className="panel-title">Referral goal</div>
        {goalQ.error && <div className="error-banner">{goalQ.error}</div>}
        {goalError && <div className="error-banner">Could not save: {goalError}</div>}
        <div className="flex flex-wrap" style={{ alignItems: "flex-end" }}>
          <div className="field" style={{ maxWidth: 240, marginBottom: 0 }}>
            <label htmlFor="referral-goal">Referred clients to aim for</label>
            <input
              id="referral-goal"
              type="number"
              min={1}
              step={1}
              value={goalDraft}
              placeholder="e.g. 25"
              disabled={goalQ.loading || goalSaving}
              onChange={(e) => setGoalDraft(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => void saveGoal()}
            disabled={goalSaving || goalQ.loading || !goalValid || !goalDirty}
          >
            {goalSaving ? "Saving…" : "Save goal"}
          </button>
        </div>
        {!goalValid && goalDraft.trim() !== "" && (
          <div className="field-hint amber mt-1">Enter a whole number of 1 or more.</div>
        )}
        {goal == null && !goalQ.loading && !goalQ.error && (
          <div className="field-hint mt-1">
            No goal set yet — pick how many referred clients to chase and save it.
          </div>
        )}
        {goal != null && progressPct != null && (
          <div className="mt-2">
            <div className="flex" style={{ justifyContent: "space-between" }}>
              <span className="small bold">
                {referredCount} of {goal} referred clients
              </span>
              <span className="small muted">{pct1(progressPct)}%</span>
            </div>
            <div
              style={{
                height: 14,
                borderRadius: 999,
                background: "var(--panel-2)",
                border: "1px solid var(--border)",
                overflow: "hidden",
                marginTop: 4,
              }}
              role="progressbar"
              aria-valuenow={Math.min(100, Math.round(progressPct))}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Referral goal progress"
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.min(100, progressPct)}%`,
                  borderRadius: 999,
                  background: "linear-gradient(90deg, var(--accent), var(--green))",
                  transition: "width 0.4s ease",
                }}
              />
            </div>
            {progressPct >= 100 && (
              <div className="green small bold mt-1">
                Goal hit — referrals are officially a channel. Time to raise the bar?
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
